import { QueryKeys, updateEntityVersionedAspectQuery } from '@common/queries/common';
import { Template, addTemplateQuery, updateProject } from '@common/queries/project';
import { Entity } from '@common/types/Entity';
import { versionedAspectPathResolver } from '@common/utils/misc/aspect';
import { AspectKind, BackEndCodeLanguage, BuildSystem } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Dialog, Stack } from '@prophecy/ui-v3';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useGraphQlMutation } from '../../../../../data/util';
import { useDeleteEntity } from '../../../components/DeleteEntity';
export const SettingTabKey = [QueryKeys.Project, QueryKeys.ProjectDeploymentSettings, QueryKeys.ProjectGeneralSettings];
export const useBuildSystemSwitch = ({ uid }: { uid: string }) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useGraphQlMutation(updateProject);
  const handleTemplateSwitch = async (checked: boolean) => {
    if (checked) {
      const allowedDialogResult = await Dialog.warning({
        'data-test-id': 'enable-build-system-template-warning-dialog',
        okButton: { children: 'Enable' },
        title: 'Enable Build System Template',
        size: 'l',
        children: (
          <Stack gap={theme.spaces.x12} padding={`${theme.spaces.x24} ${theme.spaces.x8}`}>
            <span>
              Enabling build-system-templating would result in re-generation of your build files (pom.xml, setup.py,
              build.sbt) for all the pipelines.
            </span>
            <span>
              If you or your teammates have never edited these files for any of the pipelines of this project. We
              recommend you to enable it
            </span>
            <span>
              If you or your teammates have at some point edited these files for pipelines of this project, please seek
              help from Prophecy Support team.
            </span>
            <a
              target='_blank'
              href='https://docs.prophecy.ai/data-analysis/development/extensibility/dependencies'
              rel='noreferrer'
              data-test-id='build-system-options-what-is-build-system-templating-link'>
              What is Build System Templating?{' '}
            </a>
          </Stack>
        )
      });
      if (allowedDialogResult !== Dialog.ResponseAction.okButton) {
        return;
      }
    }
    await mutateAsync(
      {
        uid,
        buildSystemTemplatesEnabled: checked
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QueryKeys.Git, QueryKeys.Project]);
          queryClient.invalidateQueries(SettingTabKey);
        }
      }
    );
  };

  const handleChangeLogFile = async (changelogFile: string) => {
    await mutateAsync(
      {
        uid,
        changelogFile
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QueryKeys.Git, QueryKeys.Project]);
          queryClient.invalidateQueries(SettingTabKey);
        }
      }
    );
  };
  return { handleTemplateSwitch, handleChangeLogFile, isLoading };
};

export function getTemplate(defaultTemplate: string, templates: Template[]) {
  const [firstTemplate] = templates;
  const firstTemplateData: Template | undefined = firstTemplate;
  const firstTemplateValue = firstTemplateData
    ? (versionedAspectPathResolver(
        AspectKind.Configuration,
        firstTemplateData.versionedAspects || [],
        'template.yml'
      ) as string)
    : undefined;
  const firstTemplateId = firstTemplateData?._id;
  const initialValue = firstTemplateValue || defaultTemplate;
  return { templateId: firstTemplateId, templateValue: initialValue };
}
export function useBuildSystem({
  uid,
  language,
  template,
  templateId = ''
}: {
  uid: string;
  language: string;
  template: string;
  templateId?: string;
}) {
  const addMutation = useGraphQlMutation(addTemplateQuery);
  const [showDiff, toggleDiff] = useState(false);
  const queryClient = useQueryClient();
  const versionedAspectMutation = useGraphQlMutation(updateEntityVersionedAspectQuery);

  const isPython = language === BackEndCodeLanguage.python;
  const entityName = isPython ? BuildSystem.wheel : BuildSystem.maven;

  const { remove: restoreDefaultTemplate, mutation: restoreDefaultTemplateMutation } = useDeleteEntity({
    entityId: templateId,
    entity: Entity.Template,
    showConfirmation: false,
    successMessage: 'Template restored successfully'
  });

  const handleSubmit = async () => {
    let id = templateId;
    if (!id) {
      await addMutation.mutateAsync(
        {
          uid,
          name: entityName
        },
        {
          onSuccess: (resp) => {
            id = resp?.addTemplate?._id;
          }
        }
      );
    }
    const aspectValue = JSON.stringify({ 'template.yml': template });
    await versionedAspectMutation.mutateAsync(
      {
        uid: id,
        entityKind: Entity.Template,
        aspectKind: 'Configuration',
        aspectValue
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QueryKeys.Git, QueryKeys.Project]);
        }
      }
    );
  };
  const isUpdating = addMutation.isLoading || versionedAspectMutation.isLoading;
  return {
    showDiff,
    toggleDiff,
    restoreDefaultTemplateMutation: {
      isLoading: restoreDefaultTemplateMutation.isLoading,
      mutateAsync: restoreDefaultTemplate
    },
    handleSubmit,
    isUpdating
  };
}
